import ContentLoader from 'react-content-loader';
import type { VoidFunctionComponent } from 'react';

import { BINARY_DECOMPOSITION } from '../../../../shared/featureFlags/flagNames';
import { plural } from '../../../../shared/helpers/plural';
import { PREMIUM_PER_REVISION_PLAN_NAME } from '../../../../shared/subscriptions/consts';
import { window } from '../../../globals';
import { useIsFeatureEnabled } from '../../../helpers/features/featureFlags';
import { useGetCurrentPlan } from '../../../hooks/api/subscriptions/useGetCurrentPlan';
import COLORS from '../../../theme/colors';
import Badge from '../../generic/Badge/Badge';
import Flex from '../../generic/Flex';

import UsageBadge from './UsageBadge';
/**
 * Loader for revision usage badge
 */
const Loader: VoidFunctionComponent = () => (
  <ContentLoader
    width="140px"
    height="40px"
    viewBox="0 0 140 12"
    backgroundColor={COLORS.fossaGray60}
    foregroundColor={COLORS.fossaGray40}
  >
    <rect x="0" y="0" rx="3" ry="3" width="100%" height="12" />
  </ContentLoader>
);

const UsageBadges: VoidFunctionComponent = () => {
  const { data, isLoading } = useGetCurrentPlan();
  const isPerRevisionSubscriber = window.user?.organization?.stripe_plan === PREMIUM_PER_REVISION_PLAN_NAME;
  const isBinaryDecompEnabled = useIsFeatureEnabled(BINARY_DECOMPOSITION);

  if (isLoading) {
    return (
      <Badge
        color="alert-info"
        size="medium"
      >
        <Loader />
      </Badge>
    );
  }

  return (
    <Flex gap={8}>
      {isPerRevisionSubscriber && data?.usage?.revision && (
        <UsageBadge
          limitUnit={plural(data.usage.revision.quantity, 'revision')}
          limit={data.usage.revision.quantity}
          usage={data.usage.revision.usage}
          usageTooltip={
            <span>
              Your organization is subscribed to our <b>Revision-based Pricing</b>.
              {' ' /* a space wasn't being rendered here */}
              <b>Contact Sales</b> or visit your <b>Billing page</b> to learn more about this.
            </span>
          }
        />
      )}
      {isBinaryDecompEnabled && data?.usage?.binary && (
        <>
          <UsageBadge
            limitUnit={plural(data.usage.binary.quantity, 'binary', 'binaries')}
            limit={data.usage.binary.quantity}
            usage={data.usage.binary.usage}
            usageTooltip={
              <>
                The number of binaries you can decompose.
              </>
            }
            atLimitTooltip={
              <>
                <div>
                  You are out of binary decompositions.
                </div>
                <div>
                  Contact us to increase this amount.
                </div>
              </>
            }
          />
          {/* Always show this badge when binary decomp is enabled */}
          <Badge color="darker-gray" size="small">
            Unlimited projects
          </Badge>
        </>
      )}
    </Flex>
  );
};

const UsageBadgeContainer: VoidFunctionComponent = () => {
  const isPerRevisionSubscriber = window.user?.organization?.stripe_plan === PREMIUM_PER_REVISION_PLAN_NAME;
  const isBinaryDecompEnabled = useIsFeatureEnabled(BINARY_DECOMPOSITION);

  if (!isPerRevisionSubscriber && !isBinaryDecompEnabled) {
    return null;
  }

  return (
    <UsageBadges />
  );
};

export default UsageBadgeContainer;
